import { axiosCustom, getJobResultExcelStyle } from 'common/common.function';
import Button from 'components/UI/atoms/Button';
import Grid from 'components/UI/atoms/Grid';
import TableCheckBox from 'components/UI/atoms/TableCheckBox';
import TextCard from 'components/UI/atoms/TextCard';
import TextCardWrap from 'components/UI/atoms/TextCardWrap';
import TextField from 'components/UI/atoms/TextField';
import FormWrap from 'components/UI/molecules/FormWrap';
import InputWrap from 'components/UI/molecules/InputWrap';
import React, { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ThemeProvider } from 'styled-components';
import theme from 'styles/theme';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import TextEditor from 'components/UI/atoms/TextEditor';
import XLSX from 'xlsx';
import { ICoreOutput } from 'common/common.interface';

/**
 * @alias 문의 에러 발생 지점
 * @description bedrock agent 질문용 enum
 */
enum ErrorPoint {
  /**
   * @alias 상품 수집 중 실패
   */
  SCRAP = 'SCRAP',
  /**
   * @alias 상품 등록 중 실패
   */
  REGIST = 'REGIST',
  /**
   * @alias 상품 등록 성공
   * @description 상품 등록은 성공 했으나 의도한대로 등록 되지 않은 경우의 문의
   */
  FINISH = 'FINISH',
}

/**
 * @alias Bedrock Agent 질문용 Object Interface
 */
interface BotObject {
  /**
   * @alais 상품 수집 쇼핑몰 명칭
   */
  scrapProdName: string;
  /**
   * @alais 상품 등록 쇼핑몰 명칭
   */
  registShopName: string;
  /**
   * @alais 에러 지점
   */
  errorPoint: ErrorPoint;
  /**
   * @alais 에러 메시지
   */
  errorMessage: string;
}

interface DetailObject {
  /**
   * @alais 수집한 쇼핑몰 상품코드
   */
  scrapProdCode: string;
  /**
   * @alais 문의용 작업 번호
   */
  childJobId?: number;
  /**
   * @alais 문의 내용
   */
  content: string;
  /**
   * @alais 문의 인덱스
   * @description ex ) 문의 1, 문의 2
   */
  prefix: string;
}

interface RedmineObject {
  /**
   * @alais 웨이크업 가입 아이디
   */
  wakeupId: string;
  /**
   * @alias 레드마인 문의 상세 내용
   */
  detailObject: DetailObject[];
  /**
   * @alias bedrock agent 용 레드마인 문의 내용
   */
  botObject: BotObject[];
}

interface SendRedmineProps {
  /**
   * @alias parent job id
   */
  jobId: number;
  /**
   * @alias solType
   */
  solType: string;
  /**
   * @alais 자식 작업 카운트
   */
  count: number;

  /**
   * @alias 웨이크업 아이디
   */
  email: string;

  /**
   * @alias 이동하는 쇼핑몰 이름
   */
  targetShopName: string;

  /**
   * @alias 가져오는 쇼핑몰 이름
   */
  scrapShopName: string;

  /**
   * @alias 실패 메시지
   */
  failureMessage?: string;

  /**
   * @alias 작업 상태
   */
  state: string;

  /**
   * @alias 성공 카운트
   */
  successCount: number;

  /**
   * @alias 실패 카운트
   */
  failureCount: number;
}
interface SendRedmineInput {
  /**
   * @alias pawn admin 기준 아이디
   * @description ex: kaya
   */
  userId: string;
  /**
   * @alias 문의 내용
   */
  description: string;

  /**
   * @alias parent job Id
   */
  parentJobId: number;

  /**
   * @alias 이미지파일1
   */
  productImgFile1: string;
  /**
   * @alias 이미지파일2
   */
  productImgFile2: string;
  /**
   * @alias 이미지파일3
   */
  productImgFile3: string;
  /**
   * @alias 이미지파일4
   */
  productImgFile4: string;
  /**
   * @alias 이미지파일5
   */
  productImgFile5: string;
}

const ErrorMessage = styled.p`
  color: red;
  text-align: center;
`;

const ButtonTag = styled(Button)`
  padding: 0 18px;
  height: 36px;
  line-height: 34px;
  font-size: 16px;
  font-weight: 500;
  background: #fff;
`;

export default function SendRedmine({
  jobId,
  solType,
  count,
  email,
  scrapShopName,
  targetShopName,
  failureMessage,
  state,
  successCount,
  failureCount,
}: SendRedmineProps) {
  const { register, handleSubmit, setValue } = useForm<SendRedmineInput>({ mode: 'onSubmit' });

  // 잡디테일 데이터
  const [jobDetailData, setJobDetailData] = useState<any[]>([]);

  // 컴포넌트의 로딩 상태 관리
  const [loading, setLoading] = useState(true);

  // 에러메시지
  const [error, setError] = useState<string | null>(null);

  // 조회 값
  const [searchValue, setSearchValue] = useState('');

  // 엑셀 다운로드 아이콘
  const [showDownloadIcon, setShowDownloadIcon] = useState(false);

  // 레드마인 관리 obj
  const [redmineObject, setRedmineObject] = useState<RedmineObject>({
    wakeupId: '',
    detailObject: [],
    botObject: [],
  });

  //첨부파일 관리 훅
  const [files, setFiles] = useState<File[]>([]);

  // parent job id만 존재할때의 상태 관리
  const [parentJobWithoutChild, setParentJobWithoutChild] = useState(false);

  // 파일 입력 요소에 접근하기 위한 useRef 훅 사용
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * @alais 파일 첨부 클릭 핸들러
   * @description 파일 첨부 버튼 클릭 시, 숨겨진 파일 입력 요소를 클릭하여 파일 선택 창을 열기 위한 함수
   */
  const attatchFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * @alais 파일 삭제 클릭 핸들러
   */
  const removeFileClick = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  /**
   * @alais 파일선택했을때 호출되는 함수
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFile = event.target.files[0];
      if (newFile) {
        if (files.length >= 5) {
          /////이부분 seterror처리 ?
          alert('최대 5개의 파일만 첨부할 수 있습니다.');
          return;
        }
        const isDuplicate = files.some((file) => file.name === newFile.name);
        /////이부분 seterror처리 ?
        if (isDuplicate) {
          alert('동일한 파일이 존재합니다.');
          return;
        }
        setFiles((prevFiles) => [...prevFiles, newFile]);
        event.target.value = '';
      }
    }
  };

  /**
   * @alias parent jobid만 존재할때의 문의 내용 기본값 설정
   */
  const handleParentJobWithoutChild = async (jobId: number, failMessage: string, state: string, successCount: number, failureCount: number) => {
    const botObject: BotObject = {
      errorMessage: failMessage,
      errorPoint: ErrorPoint.FINISH,
      scrapProdName: scrapShopName,
      registShopName: targetShopName,
    };
    let content = `작업번호 : ${jobId}</br>`;
    // 만약 실패메시지가 존재한다면 C#의 scrap prod 에서 실패
    if (failMessage) {
      botObject.errorPoint = ErrorPoint.SCRAP;
      content += `수집 실패 메세지 : ${failMessage}</br>`;
    }
    // 성공 실패 카운트가 0 이고, state가 FAILURE 이라면
    // 1. addJob 요청 후 Job 테이블에는 작업 정보가 inactive로 저장
    // 2. 1번에서 건 addJob 작업의 큐가 밀려서 실제 작업이 진행되지 않음
    // 3. 추후 job 테이블의 state는 미완료 작업을 실패처리하는 스케줄러로 인해 inactive -> failure로 변경
    else if (state === 'FAILURE' && successCount === 0 && failureCount === 0) {
      content += `수집 실패 메세지 : add job 작업이 정상적으로 진행되지 않았습니다. Medis에서 해당 계정의 작업이 inactive인지 확인하고 refreshAllJobs를 호출해주세요.</br>`;
    }

    const selectedObject = {
      scrapProdCode: 'NULL',
      content: content,
      prefix: '',
    };
    // detailObject에 selectedObject를 추가하고, 각 항목을 업데이트
    const updatedDetailObject = [...redmineObject.detailObject, selectedObject].map((one, index) => ({
      ...one,
      prefix: fommatingQuestionMark(index),
      content: one.content,
    }));
    setRedmineObject((prev) => ({
      ...prev,
      detailObject: updatedDetailObject,
      botObject: [...prev.botObject, botObject],
    }));
  };

  /**
   * @alais  3개이상 데이터를 선택하면 에러메시지
   */
  useEffect(() => {
    if (redmineObject.detailObject.length > 3) {
      setError('최대 3개까지 선택 가능합니다.');
    } else {
      setError(null);
    }
  }, [redmineObject]);

  /**
   * @alais jobdetail 데이터 불러오기
   */
  useEffect(() => {
    const fetchJobDetail = async () => {
      if (count < 100) {
        try {
          const response = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/${jobId}`, {
            timeout: 300000,
            headers: {
              'sol-type': solType,
            },
          });
          // 모든 데이터는 100개까지만 저장해서 보여준다.
          setJobDetailData(response.result);
        } catch (error: any) {
          // 만약 parent jobId만 존재하고 child jobId가 없을때는(detail-job이 조회되지 않는 경우)
          // 텍스트필드에 parent jobId를 보여준다.
          if (error.message.includes('작업정보가 조회되지 않습니다')) {
            setParentJobWithoutChild(true);
            // 만약 실패 메시지가 존재한다면 수집 실패 메시지로 넣어준다
            if (failureMessage) {
              handleParentJobWithoutChild(jobId, failureMessage, state, successCount, failureCount);
            } else {
              handleParentJobWithoutChild(jobId, '', state, successCount, failureCount);
            }
          } else {
            await Swal.fire({
              title: '실패',
              html: `작업 정보 불러오기 중 실패 ${error.message}`,
              icon: 'error',
            });
          }
        } finally {
          setLoading(false);
        }
      } else {
        //만약 100개 이상이면 100개까지만 보여주고 엑셀 다운로드 아이콘 보여줌
        const response = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/${jobId}?isLimit=true`, {
          timeout: 300000,
          headers: {
            'sol-type': solType,
          },
        });
        setJobDetailData(response.result);
        setShowDownloadIcon(true);
        setLoading(false);
      }
    };

    fetchJobDetail();
  }, [jobId, solType]);

  // 테이블이 폼보다 나중에 조회돼서 loading 띄워줌
  if (loading) return <p>Loading...</p>;

  /**
   * @alais 문의 폼 레드마인으로 전송
   */
  const onSubmit: SubmitHandler<SendRedmineInput> = async (data) => {
    Swal.fire({
      title: '등록 중...',
      text: '잠시만 기다려 주세요.',
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const formData = new FormData();

    const jwt = localStorage.getItem('jwt');
    formData.append('description', data.description);
    formData.append('parentJobId', jobId.toString());
    formData.append('email', email);
    formData.append('scrapShopName', scrapShopName);
    formData.append('targetShopName', targetShopName);
    formData.append('botObject', JSON.stringify(redmineObject.botObject));
    formData.append(
      'jobItem',
      JSON.stringify(
        redmineObject.detailObject.map((one) => ({
          prodCode: one.scrapProdCode,
          jobId: one.childJobId,
        })),
      ),
    );
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response: ICoreOutput = await axiosCustom.post(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/send-redmine`, formData, {
        headers: {
          jwt: jwt,
        },
      });
      if (response.success) {
        Swal.fire('완료', '성공적으로 제출되었습니다.', 'success');
      } else {
        await Swal.fire({
          title: '실패',
          html: `[레드마인 등록 중 오류1] - ${response.message}`,
          icon: 'error',
          showConfirmButton: true,
        });
      }
    } catch (error: any) {
      await Swal.fire({
        title: '실패',
        html: `[레드마인 등록 중 오류2] - ${error.message}`,
        icon: 'error',
        showConfirmButton: true,
      });
    }
  };

  /**
   * @alais 선택한 작업에 번호 리스팅하는 함수
   * @description 작업 1 , 작업 2  ...
   */
  function fommatingQuestionMark(index: number) {
    return `<p style="font-size: 20px;"><b>문의 ${index + 1}</b></p>`;
  }

  /**
   * @alais 상품코드로 데이터 검색
   * @description 상품번호로 작업번호 + 에러메세지 조회
   */
  async function searchProdCode(scrapProdCode: string) {
    if (redmineObject.detailObject.length >= 3) {
      setError('최대 3개까지 선택 가능합니다.');

      return false;
    }
    const isDuplicate = redmineObject.detailObject.some((item) => item.scrapProdCode === scrapProdCode);
    if (isDuplicate) {
      setError('중복된 상품코드 입니다.');
      return false;
    }

    try {
      const response = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/sendRedmine-detail/${jobId}/${scrapProdCode}`, {
        timeout: 300000,
      });

      if (response.success) {
        const botObject: BotObject = {
          errorMessage: '',
          errorPoint: ErrorPoint.SCRAP,
          scrapProdName: scrapShopName,
          registShopName: targetShopName,
        };
        let scrapFailMessage = '';
        let registFailMessage = '';

        if (response.result.scrapResult === 'FAILURE') {
          scrapFailMessage = response.result.scrapMessage;
          botObject.errorMessage = response.result.scrapMessage;
          botObject.errorPoint = ErrorPoint.SCRAP;
        } else if (response.result.apiResult === 'FAILURE') {
          registFailMessage = response.result.apiMessage;
          botObject.errorMessage = response.result.apiMessage;
          botObject.errorPoint = ErrorPoint.REGIST;
        } else {
          // 등록 성공한 상품 문의 시 에러 메시지 빈 값 / 에러 포인트 FINISH
          botObject.errorMessage = '';
          botObject.errorPoint = ErrorPoint.FINISH;
        }

        let content = `상품코드 : ${response.result.scrapProdCode} </br>작업번호 : ${response.result.jobId}</br>`;
        if (scrapFailMessage) {
          content += `수집 실패 메세지 : ${scrapFailMessage} </br>`;
        } else if (registFailMessage) {
          content += `등록 실패 메세지 : ${registFailMessage}</br>`;
        }
        const selectedObject = {
          scrapProdCode: response.result.scrapProdCode,
          childJobId: response.result.jobId,
          content: content,
          prefix: '',
        };

        redmineObject.detailObject.push(selectedObject);

        const updatedDetailObject = redmineObject.detailObject.map((one, index) => {
          return {
            ...one,
            prefix: fommatingQuestionMark(index),
            content: one.content,
          };
        });

        setRedmineObject((prev) => ({
          ...prev,
          detailObject: updatedDetailObject,
          botObject: [...prev.botObject, botObject],
        }));
      } else {
        setError('존재하지 않는 상품코드입니다. 다시 확인해주세요.');
      }
    } catch (error: any) {
      await Swal.fire({
        title: '실패',
        html: `[상품코드로 데이터 검색 중 에러] - ${error.message}`,
        confirmButtonText: '확인',
      });
    }
  }

  /**
   * @alias 레드마인 올리기 > 작업 상세보기 엑셀 다운로드
   * @description 100개이상 데이터만 엑셀 다운로드 아이콘 보여줌
   */
  async function jobExcelEvent(type: 'EXCEL' | 'VIEW', jobId: number, count: number) {
    try {
      const { result: jobDetailData } = await axiosCustom.get(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/${jobId}`, {
        timeout: 300000,
        headers: {
          'sol-type': solType,
        },
      });

      if (type === 'EXCEL') {
        const dataWS = XLSX.utils.json_to_sheet(jobDetailData);
        // 엑셀 컬럼 너비 정하기
        // 여기서는 tratget soltype을 불러오기엔 api를 한번 더 호출해야해서 기본값으로 너비 설정
        dataWS['!cols'] = getJobResultExcelStyle('ADMIN');
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, dataWS, 'jobDetailData');
        XLSX.writeFile(wb, `${jobId}.xlsx`);
      }
    } catch (error: any) {
      await Swal.fire({
        title: '실패',
        html: `[문의용 엑셀 다운로드 중 에러] - ${error.message}`,
        confirmButtonText: '확인',
      });
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <strong style={{ display: 'block', fontSize: '20px', color: '#333', letterSpacing: '-0.6px', textAlign: 'left' }}>
        {parentJobWithoutChild ? '문의 내용을 작성해주세요.' : '문의할 작업 선택'}
      </strong>
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div style={{ textAlign: 'left', fontWeight: 500 }}>
          {parentJobWithoutChild ? (
            <>
              <p style={{ color: '#0072CE' }}>※ 해당 작업은 작업 세부내역이 존재하지 않습니다.</p>
              <p>※ 문의내용을 작성하면 해당 계정의 웨이크업 아이디와 작성하신 내용이 레드마인에 전송됩니다.</p>
            </>
          ) : (
            <>
              <p style={{ color: '#0072CE' }}>※ 작업 상세 데이터는 최대 100개까지만 출력됩니다.</p>
              <p>※ 아래의 테이블에 나오지 않는 데이터는 엑셀파일을 다운받은 후 상품코드를 직접 조회해서 찾아주세요.</p>
            </>
          )}
        </div>

        <div>
          {showDownloadIcon ? (
            <ButtonTag
              color="main"
              size="small"
              variant="outlined"
              width={130}
              borderRadius={18}
              onClick={() => {
                jobExcelEvent('EXCEL', jobId, count);
              }}
            >
              엑셀다운로드
            </ButtonTag>
          ) : null}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormWrap redMine={true}>
          {jobDetailData.length > 0 && (
            <TableCheckBox
              headerItems={Object.keys(jobDetailData[0]).map((one, index) => ({
                width:
                  index === 0
                    ? 110
                    : index === 1
                    ? 130
                    : index === 2
                    ? 160
                    : index === 3
                    ? 160
                    : index === 4
                    ? 220
                    : index === 5
                    ? 200
                    : index === 6
                    ? 220
                    : index === 7
                    ? 180
                    : index === 8
                    ? 230
                    : index === 9
                    ? 160
                    : index === 10
                    ? 220
                    : 100,
                align: 'center',
                headerAlign: 'center',
                headerName: one,
                editable: true,
              }))}
              bodyItems={jobDetailData.map((prodOne: any) => Object.keys(prodOne).map((one) => prodOne[one]))}
              onSelectionChange={async (data: any) => {
                if (redmineObject.detailObject.length + data.length > 3) {
                  setError('최대 3개까지 선택 가능합니다.');
                  return false;
                }

                const botObjects: BotObject[] = [];
                data.map((item: any, index: number) => {
                  const botObject: BotObject = {
                    errorMessage: '',
                    errorPoint: ErrorPoint.SCRAP,
                    scrapProdName: scrapShopName,
                    registShopName: targetShopName,
                  };
                  let scrapFailMessage = '';
                  let registFailMessage = '';

                  if (item.col2 === '실패') {
                    botObject.errorPoint = ErrorPoint.SCRAP;
                    botObject.errorMessage = item.col3;
                    scrapFailMessage = item.col3;
                  } else if (item.col5 === '실패') {
                    botObject.errorPoint = ErrorPoint.REGIST;
                    botObject.errorMessage = item.col6;
                    registFailMessage = item.col6;
                  } else {
                    botObject.errorPoint = ErrorPoint.FINISH;
                    botObject.errorMessage = '';
                  }

                  let content = `상품코드 : ${item.col1} </br>작업번호 : ${item.col0}</br>`;

                  if (scrapFailMessage) {
                    content += `수집 실패 메세지 : ${scrapFailMessage} </br>`;
                  } else if (registFailMessage) {
                    content += `등록 실패 메세지 : ${registFailMessage}</br>`;
                  }
                  const selectedObject = {
                    scrapProdCode: item.col1,
                    childJobId: item.col0,
                    content: content,
                    prefix: fommatingQuestionMark(redmineObject.detailObject.length),
                  };

                  redmineObject.detailObject.push(selectedObject);
                  botObjects.push(botObject);
                });

                setRedmineObject((prev) => ({
                  ...prev,
                  detailObject: redmineObject.detailObject,
                  botObject: [...prev.botObject, ...botObjects],
                }));
              }}
            />
          )}

          <FormWrap.Top>
            {parentJobWithoutChild ? null : (
              <Grid top={10}>
                <InputWrap
                  label="상품 코드"
                  labelWidth={140}
                  input={
                    <>
                      <TextField type="string" value={searchValue} variant="filled" onChange={(event) => setSearchValue(event.target.value.trim())} />
                      {redmineObject.detailObject.length > 0 && redmineObject.detailObject[0].scrapProdCode !== 'NULL' ? (
                        <TextCardWrap
                          item={redmineObject.detailObject.map(({ scrapProdCode }) => (
                            <TextCard
                              text={scrapProdCode}
                              onClick={async () => {
                                redmineObject.detailObject.map((one, index) => {
                                  if (one.scrapProdCode === scrapProdCode) {
                                    redmineObject.detailObject.splice(index, 1);
                                    redmineObject.botObject.splice(index, 1);
                                  }
                                });

                                const updateddetailObject = redmineObject.detailObject.map((one, index) => {
                                  return {
                                    ...one,
                                    prefix: fommatingQuestionMark(index),
                                    content: one.content,
                                  };
                                });
                                setRedmineObject((prev) => ({
                                  ...prev,
                                  detailObject: updateddetailObject,
                                  botObject: redmineObject.botObject,
                                }));
                              }}
                            />
                          ))}
                        ></TextCardWrap>
                      ) : null}
                      {error && <ErrorMessage>{error}</ErrorMessage>}
                    </>
                  }
                  adornment={
                    <Button
                      width={105}
                      size="small"
                      color="main"
                      variant="outlined"
                      borderRadius={0}
                      type="button"
                      onClick={() => {
                        searchProdCode(searchValue);
                        setSearchValue('');
                      }}
                    >
                      조회
                    </Button>
                  }
                />
              </Grid>
            )}

            {/* <Grid top={10}>
              <InputWrap label="작업번호" labelWidth={140} input={<TextField defaultValue={parentJobId} type="text" variant='filled' />} />
            </Grid> */}
            <Grid top={10}>
              <InputWrap
                label="내용"
                labelWidth={140}
                input={
                  <TextEditor
                    {...register('description', {
                      required: {
                        message: '상세 내용은 필수 입력 항목입니다.',
                        value: true,
                      },
                      onChange: (e) => setValue('description', e), // 에디터의 내용을 폼 상태에 반영
                    })}
                    placeholder={
                      `웨이크업 아이디 : ${email}\n` +
                      (redmineObject.detailObject.length > 0
                        ? redmineObject.detailObject.map(({ prefix, content }) => `${prefix}${content}`).join('</br>')
                        : '')
                    }
                    onChange={(content) => setValue('description', content)} // 폼 상태 업데이트
                  />
                }
              />
            </Grid>
            <Grid top={15}>
              <InputWrap
                label="파일 첨부"
                labelWidth={140}
                input={
                  <div style={{ display: 'flex' }}>
                    <TextField type="file" id="file-attach" variant="none" {...register('productImgFile1')} ref={fileInputRef} onChange={handleFileChange} />
                    <Button width={124} size="small" color="main" variant="outlined" borderRadius={18} type="button" onClick={attatchFileClick}>
                      파일첨부
                    </Button>

                    {/* 첨부 후 */}
                    <ul style={{ marginLeft: '20px', display: 'flex', alignItems: 'center' }}>
                      {files.map((file, index) => (
                        <li style={{ marginRight: '20px', lineHeight: 1 }}>
                          <span style={{ fontSize: '16px', textDecoration: 'underline' }}>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFileClick(index)}
                            style={{ marginLeft: '2px', fontSize: '14px', border: 0, background: 'transparent', cursor: 'pointer' }}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                }
              />
            </Grid>
          </FormWrap.Top>
          <FormWrap.Bottom>
            <Grid display="flex" justifyContent="center">
              <Button type="submit" color="main" variant="contained" size="medium" width={200} height={60} borderRadius={0}>
                레드마인
              </Button>
            </Grid>
          </FormWrap.Bottom>
        </FormWrap>
      </form>
    </ThemeProvider>
  );
}
